import { getUserVerificationDetails, UserDetails, VerificationVersionType } from "helpers/backend_helper";
import { CustomSelect } from "Components/CustomSelect";
import { DefaultOptionType } from "antd/es/select";
import verifivationFailed from "assets/svg/verificationFailed.svg";
import verifivationSuccess from "assets/svg/verificationSuccess.svg";
import sealCheckBackground from "assets/svg/SealCheckWithoutBackground.svg";
import sealWarningBackground from "assets/svg/SealWarning.svg";
import styles from "./index.module.css";
import { ModalComponentV1 } from "Components/ModalComponentV1";
import { useContext, useEffect, useMemo, useState } from "react";
import { Col, Row } from "antd";
import { NotificationContext } from "context/notificationContext";
import { useRequest } from "ahooks";
import { AdminContext } from "context/adminContext";

type DataVerifType = { label: string; c2c: string; kyc: string };

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  userInfo: UserDetails;
  verificationVersions: VerificationVersionType[];
  countryUserId: string;
};

const OptionSelect = (isSuccess: boolean, value: string) => (
  <div className={styles.optionSelect}>
    <img src={isSuccess ? verifivationSuccess : verifivationFailed} alt="icon status of verification" /> {value}
  </div>
);

export const VerificationModal = ({ isOpen, onCloseModal, userInfo, countryUserId, verificationVersions }: Props) => {
  const { openNotificationWithIcon } = useContext(NotificationContext);

  const [choosen, setChoosen] = useState<string>("0");
  const [options, setOptions] = useState<DefaultOptionType[]>([]);
  const { localCountry } = useContext(AdminContext);

  const { data: verificationUserDetails, runAsync: runGetVerificationDetails } = useRequest(
    (choosenProfileId: string) =>
      getUserVerificationDetails(countryUserId, choosenProfileId, localCountry?.iso.toUpperCase()),
    {
      ready: !!countryUserId && !!localCountry?.iso,
      manual: true,
      onError: (err: any) => {
        openNotificationWithIcon("error", err.response?.data?.message || err?.message);
      },
    },
  );

  useEffect(() => {
    if (!verificationVersions?.length) return;
    setOptions(
      verificationVersions.map((v, i) => ({
        value: i.toString(),
        label: new Date(v.createdAt).toLocaleString().split(",")[0],
        isSuccess: v.status === "APPROVED",
      })),
    );
    runGetVerificationDetails(verificationVersions[0].id);
  }, [verificationVersions]);

  const currentOptionData = useMemo((): any => {
    return options.find((v) => v.value === choosen);
  }, [choosen, options]);

  const data: DataVerifType[] = useMemo(() => {
    if (!verificationUserDetails?.veriffData || !userInfo) return [];

    const res: DataVerifType[] = [];

    res.push({
      label: "First name",
      c2c: userInfo.firstName,
      kyc: verificationUserDetails.veriffData?.person?.firstName,
    });
    res.push({
      label: "Last name",
      c2c: userInfo.lastName,
      kyc: verificationUserDetails.veriffData?.person?.lastName,
    });
    res.push({
      label: "Address",
      c2c: userInfo.address,
      kyc: verificationUserDetails.veriffData?.address,
    });
    res.push({
      label: "Date of birth",
      c2c: null,
      kyc: verificationUserDetails.veriffData?.person?.dateOfBirth,
    });
    if (
      (verificationUserDetails.veriffData?.vendorData && verificationUserDetails.clientType === "BUSINESS") ||
      (verificationUserDetails.clientType === "BUSINESS" && verificationUserDetails?.businessInfo?.companyName)
    ) {
      const data = verificationUserDetails?.businessInfo?.companyName
        ? verificationUserDetails?.businessInfo?.companyName
        : JSON.parse(verificationUserDetails.veriffData?.vendorData);
      res.push({
        label: "Company Name",
        c2c: null,
        kyc: verificationUserDetails?.businessInfo?.companyName
          ? verificationUserDetails?.businessInfo?.companyName
          : data?.companyName,
      });
      res.push({
        label: "NIF",
        c2c: null,
        kyc: verificationUserDetails?.businessInfo?.nif ? verificationUserDetails?.businessInfo?.nif : data?.nif,
      });
      res.push({
        label: "Company Address",
        c2c: null,
        kyc: verificationUserDetails?.businessInfo?.companyAddress
          ? verificationUserDetails?.businessInfo?.companyAddress
          : data.companyAddress,
      });
    }
    res.push({
      label: "Gender",
      c2c: null,
      kyc: verificationUserDetails.veriffData.person?.gender,
    });
    res.push({
      label: "Nationality",
      c2c: null,
      kyc: verificationUserDetails.veriffData.person?.nationality,
    });
    res.push({
      label: "Date of expiry",
      c2c: null,
      kyc: verificationUserDetails.expiresAt,
    });
    res.push({
      label: "Document number",
      c2c: null,
      kyc: verificationUserDetails.veriffData?.document?.number,
    });
    res.push({
      label: "Date of issue",
      c2c: null,
      kyc: verificationUserDetails.veriffData?.document?.validFrom,
    });
    res.push({
      label: "Personal number",
      c2c: null,
      kyc: null,
    });

    return res;
  }, [verificationUserDetails, userInfo]);

  return (
    <ModalComponentV1 isOpen={isOpen} onClose={onCloseModal} title="KYC">
      <div>
        <div className={styles.verifDate}>
          <span>Verification Date</span>
          <CustomSelect
            options={options}
            value={choosen}
            optionRender={(props: any) => {
              return OptionSelect(props.data.isSuccess, props.data.label);
            }}
            onChange={(v) => {
              setChoosen(v);
              runGetVerificationDetails(verificationVersions[v].id);
            }}
          />
          <div className={`${styles.security} ${currentOptionData?.isSuccess ? styles.green : styles.red}`}>
            <img src={currentOptionData?.isSuccess ? sealCheckBackground : sealWarningBackground} alt="passed" />
            <span>{currentOptionData?.isSuccess ? "Passed Security Check" : "Didn’t pass Security Check"}</span>
          </div>
        </div>
        <div className={styles.contentWrapper}>
          <Row>
            <Col span={8}></Col>
            <Col span={8}>C2C</Col>
            <Col span={7}>KYC</Col>
            <Col span={1}></Col>
          </Row>
          {data.map((item) => {
            // eslint-disable-next-line no-debugger
            const isFieldEmpty = !item.c2c || !item.kyc;
            const isSame = !isFieldEmpty ? item.c2c.trim() == item?.kyc?.trim() : false;
            const correctClassName = isFieldEmpty ? "" : `${isSame ? styles.correctItem : styles.notCorrectItem}`;
            return (
              <Row key={item.label} className={styles.dataRow}>
                <Col span={8}>
                  <span>{item.label}</span>
                </Col>
                <Col span={8}>
                  <span className={correctClassName}>{item.c2c ?? "-"}</span>
                </Col>

                <Col span={7} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className={correctClassName}>{item.kyc ?? "-"}</span>
                </Col>

                <Col span={1}>
                  {isFieldEmpty ? (
                    ""
                  ) : isSame ? (
                    <img src={verifivationSuccess} alt="icon status of verification" />
                  ) : (
                    <img src={verifivationFailed} alt="icon status of verification" />
                  )}
                </Col>
              </Row>
            );
          })}
        </div>
      </div>
    </ModalComponentV1>
  );
};
